import type { BulkOrderCreationRow } from "./bulkOrderCreationMock.ts";

export interface SuggestedShippingPo {
  sequence: number;
  totalCount: number;
  rows: BulkOrderCreationRow[];
  totalCubicFeet: number;
  utilizationPercent: number;
  shipDate: string;
  arrivalDate?: string;
}

export interface ShippingPoGenerationResult {
  suggestedPos: SuggestedShippingPo[];
  leftoverRows: BulkOrderCreationRow[];
}

export type SuggestedPoSortField =
  | "poNumber"
  | "totalCost"
  | "availableCapacity"
  | "arrivalDate"
  | "shipDate";

export type SortDirection = "ascending" | "descending";

/**
 * Illustrative first-fit bin packer, not the approved allocation algorithm.
 * Real prioritization, mixed-SKU, and tie-breaker rules are still open
 * product decisions (see BULK_ORDER_CREATION_CONTINUATION_HANDOFF.md).
 */
export function generateSuggestedShippingPos(
  rows: BulkOrderCreationRow[],
  cubeCapacityFt3: number
): ShippingPoGenerationResult {
  if (!Number.isFinite(cubeCapacityFt3) || cubeCapacityFt3 <= 0) {
    return { suggestedPos: [], leftoverRows: [...rows] };
  }

  const leftoverRows: BulkOrderCreationRow[] = [];
  const bins: BulkOrderCreationRow[][] = [];
  let currentBin: BulkOrderCreationRow[] = [];
  let currentBinCubicFeet = 0;

  for (const row of rows) {
    if (row.cubicFeet > cubeCapacityFt3) {
      leftoverRows.push(row);
      continue;
    }
    if (
      currentBin.length > 0 &&
      currentBinCubicFeet + row.cubicFeet > cubeCapacityFt3
    ) {
      bins.push(currentBin);
      currentBin = [];
      currentBinCubicFeet = 0;
    }
    currentBin.push(row);
    currentBinCubicFeet += row.cubicFeet;
  }
  if (currentBin.length > 0) bins.push(currentBin);

  const totalCount = bins.length;
  const suggestedPos = bins.map((binRows, index) => {
    const totalCubicFeet = binRows.reduce((sum, row) => sum + row.cubicFeet, 0);
    return {
      sequence: index + 1,
      totalCount,
      rows: binRows,
      totalCubicFeet: Math.round(totalCubicFeet * 10) / 10,
      utilizationPercent: Math.round((totalCubicFeet / cubeCapacityFt3) * 100),
      shipDate: binRows[0]?.shipDate ?? "",
    };
  });

  return { suggestedPos, leftoverRows };
}
